'use client';

import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { MapModal } from './MapModal';

const ECLIPSE_DATE = new Date('2026-08-12T00:00:00');

function useCountdown() {
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (now === null) return null; // SSR / pre-hydration

  const diff = ECLIPSE_DATE.getTime() - now;
  if (diff <= 0) return { days: 0, hrs: 0, min: 0, sec: 0 };

  return {
    days: Math.floor(diff / 86400000),
    hrs: Math.floor((diff % 86400000) / 3600000),
    min: Math.floor((diff % 3600000) / 60000),
    sec: Math.floor((diff % 60000) / 1000),
  };
}

export function FloatingBar() {
  const t = useTranslations('public');
  const cd = useCountdown();
  const [mapOpen, setMapOpen] = useState(false);

  return (
    <>
      {/* Floating bar — gradient fade, centered row at bottom */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none flex justify-center"
        style={{
          padding: '14px 20px 28px',
          background: 'linear-gradient(to top, var(--pub-bg) 55%, transparent)',
        }}
      >
        <div
          className="pointer-events-auto flex items-center gap-3"
          style={{
            background: 'var(--pub-surface-3)',
            border: '1px solid var(--pub-border)',
            borderRadius: 100,
            padding: '6px 16px 6px 6px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
          }}
        >
          {/* Map Orb */}
          <button
            onClick={() => setMapOpen(true)}
            className="relative flex items-center justify-center transition-all duration-200 hover:scale-105 flex-shrink-0"
            style={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              background: 'var(--pub-surface-2)',
              border: '1px solid var(--pub-border)',
            }}
            aria-label={t('liveMap')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-secondary)" strokeWidth="1.8" width="18" height="18">
              <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
              <line x1="9" y1="3" x2="9" y2="18" />
              <line x1="15" y1="6" x2="15" y2="21" />
            </svg>
            {/* Live pulse dot */}
            <span
              className="absolute"
              style={{
                top: 6,
                right: 6,
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: 'var(--pub-green)',
                boxShadow: '0 0 7px var(--pub-green-glow)',
                animation: 'pub-orb-pulse 2.2s ease-in-out infinite',
              }}
            />
          </button>

          {/* Divider */}
          <div
            style={{
              width: 1,
              height: 24,
              background: 'var(--pub-border)',
              flexShrink: 0,
            }}
          />

          {/* Countdown numbers — only render after hydration */}
          <div className="flex items-baseline gap-1.5">
            {cd ? (
              <>
                <CountdownBlock value={cd.days} unit={t('countdown.days')} />
                <CdSep />
                <CountdownBlock value={cd.hrs} unit={t('countdown.hrs')} pad />
                <CdSep />
                <CountdownBlock value={cd.min} unit={t('countdown.min')} pad />
                <CdSep />
                <CountdownBlock value={cd.sec} unit={t('countdown.sec')} pad />
              </>
            ) : (
              <>
                <CountdownBlock value={0} unit={t('countdown.days')} placeholder />
                <CdSep />
                <CountdownBlock value={0} unit={t('countdown.hrs')} pad placeholder />
                <CdSep />
                <CountdownBlock value={0} unit={t('countdown.min')} pad placeholder />
                <CdSep />
                <CountdownBlock value={0} unit={t('countdown.sec')} pad placeholder />
              </>
            )}
          </div>

          {/* Divider */}
          <div
            style={{
              width: 1,
              height: 24,
              background: 'var(--pub-border)',
              flexShrink: 0,
            }}
          />

          {/* Compact Language Switcher */}
          <CompactLangSwitcher />
        </div>
      </div>

      {/* Map Modal */}
      <MapModal open={mapOpen} onClose={() => setMapOpen(false)} />
    </>
  );
}

function CdSep() {
  return (
    <span style={{ fontFamily: 'var(--font-pub-mono)', fontSize: 12, color: 'var(--pub-text-muted)', paddingBottom: 3 }}>:</span>
  );
}

const LANGUAGES: { code: string; label: string; name: string; flag: string }[] = [
  { code: 'bg', label: 'BG', name: 'Български', flag: '🇧🇬' },
  { code: 'hr', label: 'HR', name: 'Hrvatski', flag: '🇭🇷' },
  { code: 'cs', label: 'CS', name: 'Čeština', flag: '🇨🇿' },
  { code: 'da', label: 'DA', name: 'Dansk', flag: '🇩🇰' },
  { code: 'nl', label: 'NL', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'en', label: 'EN', name: 'English', flag: '🇬🇧' },
  { code: 'et', label: 'ET', name: 'Eesti', flag: '🇪🇪' },
  { code: 'fi', label: 'FI', name: 'Suomi', flag: '🇫🇮' },
  { code: 'fr', label: 'FR', name: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'DE', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'el', label: 'EL', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'hu', label: 'HU', name: 'Magyar', flag: '🇭🇺' },
  { code: 'ga', label: 'GA', name: 'Gaeilge', flag: '🇮🇪' },
  { code: 'it', label: 'IT', name: 'Italiano', flag: '🇮🇹' },
  { code: 'lv', label: 'LV', name: 'Latviešu', flag: '🇱🇻' },
  { code: 'lt', label: 'LT', name: 'Lietuvių', flag: '🇱🇹' },
  { code: 'mt', label: 'MT', name: 'Malti', flag: '🇲🇹' },
  { code: 'pl', label: 'PL', name: 'Polski', flag: '🇵🇱' },
  { code: 'pt', label: 'PT', name: 'Português', flag: '🇵🇹' },
  { code: 'ro', label: 'RO', name: 'Română', flag: '🇷🇴' },
  { code: 'sk', label: 'SK', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'sl', label: 'SL', name: 'Slovenščina', flag: '🇸🇮' },
  { code: 'es', label: 'ES', name: 'Español', flag: '🇪🇸' },
  { code: 'sv', label: 'SV', name: 'Svenska', flag: '🇸🇪' },
  { code: 'zh-CN', label: '中文', name: '简体中文', flag: '🇨🇳' },
];

function CompactLangSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find((l) => l.code === locale) || LANGUAGES.find((l) => l.code === 'en')!;

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const switchTo = (code: string) => {
    if (code === locale) { setOpen(false); return; }
    setOpen(false);
    const pathWithoutLocale = pathname.replace(`/${locale}`, '') || '/';
    router.push(`/${code}${pathWithoutLocale}`);
  };

  return (
    <div className="relative" ref={ref}>
      {/* Circular flag button — matches map orb shape */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-center transition-all duration-200 hover:scale-105 flex-shrink-0"
        style={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          background: 'var(--pub-surface-2)',
          border: '1px solid var(--pub-border)',
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
        }}
        aria-label="Switch language"
      >
        {current.flag}
      </button>

      {open && (
        <div
          className="absolute right-0 overflow-y-auto"
          style={{
            bottom: '100%',
            marginBottom: 8,
            background: 'var(--pub-surface-2)',
            border: '1px solid var(--pub-border)',
            borderRadius: 14,
            padding: 4,
            minWidth: 180,
            maxHeight: 300,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}
        >
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === locale;
            return (
              <button
                key={lang.code}
                onClick={() => switchTo(lang.code)}
                className="w-full text-left flex items-center gap-2 transition-colors duration-100"
                style={{
                  padding: '8px 10px',
                  borderRadius: 10,
                  fontSize: 12,
                  fontFamily: 'var(--font-pub-body)',
                  color: isActive ? 'var(--pub-green)' : 'var(--pub-text-secondary)',
                  fontWeight: isActive ? 600 : 400,
                  background: isActive ? 'var(--pub-green-dim)' : 'transparent',
                  cursor: 'pointer',
                  border: 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'var(--pub-surface-3)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>{lang.flag}</span>
                <span>{lang.name}</span>
                {isActive && (
                  <span
                    className="ml-auto"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: 'var(--pub-green)',
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CountdownBlock({ value, unit, pad, placeholder }: { value: number; unit: string; pad?: boolean; placeholder?: boolean }) {
  const display = placeholder ? '--' : (pad ? String(value).padStart(2, '0') : String(value));
  return (
    <div className="text-center">
      <div
        style={{
          fontFamily: 'var(--font-pub-display)',
          fontSize: 15,
          fontWeight: 700,
          color: 'var(--pub-text-primary)',
          lineHeight: 1,
          letterSpacing: '-0.02em',
          minWidth: pad ? 18 : undefined,
        }}
      >
        {display}
      </div>
      <span
        style={{
          fontFamily: 'var(--font-pub-mono)',
          fontSize: 7,
          textTransform: 'uppercase',
          color: 'var(--pub-text-muted)',
          letterSpacing: '0.06em',
          display: 'block',
          marginTop: 1,
        }}
      >
        {unit}
      </span>
    </div>
  );
}
